import { useState, useMemo } from 'react'
import { Video, Circle, Sparkles, Crown } from 'lucide-react'
import { cn } from '@/utils/cn'
import {
  Dropdown,
  PromptInput,
  GenerateButton,
  MobileBottomBar,
  LockBadge,
  type DropdownOption,
} from '@/components/ui'
import { VIDEO_MODELS, DURATIONS, type VideoModel } from '@/config/constants'
import { redirectToVideo } from '@/utils/redirect'
import { useRedirect } from '@/contexts/redirect-context'

export function ImageToVideoForm() {
  const { openRedirect } = useRedirect()
  const [prompt, setPrompt] = useState('')
  const [model, setModel] = useState<VideoModel>('basic')
  const [duration, setDuration] = useState('3')

  const handleGenerate = () => {
    if (!prompt.trim()) return

    redirectToVideo('i2v', {
      prompt: prompt.trim(),
      model,
    })
  }

  const handleLockedClick = () => {
    openRedirect('/billing')
  }

  // Build dropdown options with lock badges
  const modelOptions: DropdownOption[] = useMemo(
    () =>
      VIDEO_MODELS.map((m) => ({
        value: m.value,
        label: m.label,
        description: m.description,
        icon: m.icon === Circle ? <Circle className="size-4" /> :
              m.icon === Sparkles ? <Sparkles className="size-4" /> :
              <Crown className="size-4" />,
        locked: m.locked,
        badge: m.locked ? <LockBadge variant={m.value === 'standard' ? 'gold' : 'violet'} /> : undefined,
      })),
    []
  )

  const durationOptions: DropdownOption[] = useMemo(() => {
    const availableDurations = DURATIONS[model] || ['3']
    return [
      { value: '3', label: '3s', locked: false },
      { value: '6', label: '6s', locked: !availableDurations.includes('6') },
      { value: '10', label: '10s', locked: !availableDurations.includes('10') },
    ].map((d) => ({
      ...d,
      badge: d.locked ? <LockBadge variant="violet" /> : undefined,
    }))
  }, [model])

  const isValid = prompt.trim()

  const generateButton = (
    <GenerateButton
      disabled={!isValid}
      className="w-full h-full"
      onClick={handleGenerate}
    >
      <Video className="size-5" />
      Generate Video
    </GenerateButton>
  )

  return (
    <>
      <div className="space-y-4 pb-24 md:pb-0">
        <PromptInput
          value={prompt}
          onChange={setPrompt}
          placeholder="Describe the motion..."
          rows={2}
        />

        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex gap-3 w-full md:w-auto">
            <div
              className={cn(
                'relative rounded-xl p-[1px] w-1/2 md:w-[160px] shrink-0',
                model === 'premium'
                  ? 'animate-border-rotate'
                  : 'bg-gradient-to-br from-white/[0.12] via-white/[0.04] to-white/[0.08]'
              )}
            >
              <Dropdown
                label="Model"
                value={model}
                onChange={(val) => setModel(val as VideoModel)}
                glow={model === 'premium'}
                onLockedClick={handleLockedClick}
                options={modelOptions}
              />
            </div>
            <div className="relative rounded-xl p-[1px] bg-gradient-to-br from-white/[0.12] via-white/[0.04] to-white/[0.08] w-1/2 md:w-auto">
              <Dropdown
                label="Duration"
                value={duration}
                onChange={setDuration}
                onLockedClick={handleLockedClick}
                options={durationOptions}
              />
            </div>
          </div>
          <div className="hidden md:block flex-1 self-stretch">
            {generateButton}
          </div>
        </div>
      </div>

      <MobileBottomBar>{generateButton}</MobileBottomBar>
    </>
  )
}
